import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface BrowserLogEntry {
  level: 'debug' | 'info' | 'log' | 'warn' | 'error';
  message: string;
  time: string;
}

export interface BrowserLogSession {
  id: string;
  startedAt: string;
  entries: BrowserLogEntry[];
}

@Injectable({
  providedIn: 'root'
})
export class BrowserLogService {
  private readonly storageKey = 'rachana-browser-logs';
  private readonly sessionStorageKey = 'rachana-browser-log-session-id';
  private readonly maxEntries = 500;
  private installed = false;
  private currentSession: BrowserLogSession = this.createSession();
  private lastSession: BrowserLogSession | null = null;

  install(): void {
    if (!environment.enableBrowserLogCapture || this.installed || typeof window === 'undefined') {
      return;
    }

    this.installed = true;
    this.initializeSessions();
    (['debug', 'info', 'log', 'warn', 'error'] as const).forEach((level) => {
      const original = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        this.add(level, args);
        original(...args);
      };
    });

    window.addEventListener('error', (event) => {
      this.add('error', ['window.error', event.message, event.filename, event.lineno, event.colno, event.error]);
    });
    window.addEventListener('unhandledrejection', (event) => {
      this.add('error', ['unhandledrejection', event.reason]);
    });
    console.info('[BrowserLog] capture installed', {
      maxEntries: this.maxEntries
    });
  }

  list(): BrowserLogEntry[] {
    return [...this.currentSession.entries];
  }

  listLastSession(): BrowserLogEntry[] {
    return [...(this.lastSession?.entries ?? [])];
  }

  listRelevant(): BrowserLogEntry[] {
    return this.filterRelevant(this.currentSession.entries);
  }

  listLastRelevant(): BrowserLogEntry[] {
    return this.filterRelevant(this.lastSession?.entries ?? []);
  }

  clear(): void {
    this.currentSession.entries = [];
    this.lastSession = null;
    this.persist();
  }

  exportText(): string {
    return [
      this.sessionToText('CURRENT', this.currentSession),
      this.lastSession ? this.sessionToText('LAST', this.lastSession) : ''
    ].filter(Boolean).join('\n\n');
  }

  private add(level: BrowserLogEntry['level'], args: unknown[]): void {
    const entry: BrowserLogEntry = {
      level,
      message: args.map((arg) => this.formatArg(arg)).join(' '),
      time: new Date().toISOString()
    };
    this.currentSession.entries = [...this.currentSession.entries, entry].slice(-this.maxEntries);
    this.persist();
  }

  private initializeSessions(): void {
    const stored = this.readStoredSessions();
    const sessionId = this.getSessionId();
    if (stored.current?.id === sessionId) {
      this.currentSession = stored.current;
      this.lastSession = stored.last;
      return;
    }

    this.currentSession = {
      id: sessionId,
      startedAt: new Date().toISOString(),
      entries: []
    };
    this.lastSession = stored.current ?? stored.last;
    this.persist();
  }

  private getSessionId(): string {
    const existing = sessionStorage.getItem(this.sessionStorageKey);
    if (existing) {
      return existing;
    }
    const id = `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(this.sessionStorageKey, id);
    return id;
  }

  private persist(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        current: this.currentSession,
        last: this.lastSession
      }));
    } catch {
      // Keep logging in memory if localStorage quota or privacy settings block writes.
    }
  }

  private readStoredSessions(): { current: BrowserLogSession | null; last: BrowserLogSession | null } {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.storageKey) || 'null') as {
        current?: BrowserLogSession;
        last?: BrowserLogSession | null;
      } | BrowserLogEntry[] | null;
      if (Array.isArray(parsed)) {
        return {
          current: {
            id: 'legacy',
            startedAt: parsed[0]?.time ?? new Date().toISOString(),
            entries: parsed.slice(-this.maxEntries)
          },
          last: null
        };
      }
      return {
        current: parsed?.current ? this.normalizeSession(parsed.current) : null,
        last: parsed?.last ? this.normalizeSession(parsed.last) : null
      };
    } catch {
      return { current: null, last: null };
    }
  }

  private normalizeSession(session: BrowserLogSession): BrowserLogSession {
    return {
      id: String(session.id || 'unknown'),
      startedAt: String(session.startedAt || new Date().toISOString()),
      entries: Array.isArray(session.entries) ? session.entries.slice(-this.maxEntries) : []
    };
  }

  private createSession(): BrowserLogSession {
    return {
      id: 'pending',
      startedAt: new Date().toISOString(),
      entries: []
    };
  }

  private filterRelevant(entries: BrowserLogEntry[]): BrowserLogEntry[] {
    return entries.filter((entry) =>
      /\[MidiRecorder\]|\[SongComposition\].*(MIDI|fit)|fit MIDI|incoming midi|midi message|stopRecording|stop MIDI|play MIDI/i.test(entry.message)
    );
  }

  private sessionToText(label: string, session: BrowserLogSession): string {
    return [
      `${label} ${session.id} started ${session.startedAt}`,
      ...session.entries.map((entry) => `${entry.time} ${entry.level.toUpperCase()} ${entry.message}`)
    ].join('\n');
  }

  private formatArg(arg: unknown): string {
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}\n${arg.stack || ''}`.trim();
    }
    if (typeof arg === 'string') {
      return arg;
    }
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  }
}
